<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import type { LocalizedCopy, SiteConfig } from '../types/site'
import {
  logVideoPlaybackError,
  logVideoPlaybackInfo,
  logVideoPlaybackSuccess,
  type PlaybackTrigger,
} from '../utils/videoDiagnostics'
import CtaButton from './CtaButton.vue'
import InlineAutoplayVideo from './InlineAutoplayVideo.vue'

defineProps<{
  config: SiteConfig
  copy: LocalizedCopy
}>()

const videoElement = ref<HTMLVideoElement | null>(null)
const autoplayBlocked = ref(false)
const isWechat = typeof navigator !== 'undefined' && /MicroMessenger/i.test(navigator.userAgent)
let interactionRecoveryInstalled = false
let blockedUiTimer: number | undefined
let playbackSucceeded = false
// 微信：桥接授权回调到来之前的 NotAllowedError 是正常现象（微信要等页面加载完才放行），
// 这段时间不弹「浏览器阻止了自动播放」，只显示封面图，等桥接回调里再试一次真失败了才提示。
let wechatBridgeAttempted = !isWechat
let blockedUiPending = false

type WeixinBridge = {
  invoke?: (method: string, params: Record<string, never>, callback: () => void) => void
}

type WechatAutoplayBootstrap = {
  requested: boolean
  resolved: boolean
  subscribe: (callback: () => void) => void
}

const wechatWindow = window as typeof window & {
  WeixinJSBridge?: WeixinBridge
  __openrareWechatAutoplay?: WechatAutoplayBootstrap
}

const removeInteractionRecovery = () => {
  if (!interactionRecoveryInstalled) return
  interactionRecoveryInstalled = false
  document.removeEventListener('touchstart', handleUserInteraction, true)
  document.removeEventListener('mousedown', handleUserInteraction, true)
  document.removeEventListener('keydown', handleUserInteraction, true)
}

const installInteractionRecovery = () => {
  if (interactionRecoveryInstalled) return
  interactionRecoveryInstalled = true
  document.addEventListener('touchstart', handleUserInteraction, { capture: true, passive: true })
  document.addEventListener('mousedown', handleUserInteraction, true)
  document.addEventListener('keydown', handleUserInteraction, true)
}

const cancelBlockedUi = () => {
  if (blockedUiTimer === undefined) return
  window.clearTimeout(blockedUiTimer)
  blockedUiTimer = undefined
}

const scheduleBlockedUi = () => {
  if (blockedUiTimer !== undefined || autoplayBlocked.value || playbackSucceeded) return
  if (!wechatBridgeAttempted) {
    blockedUiPending = true
    return
  }
  blockedUiTimer = window.setTimeout(() => {
    blockedUiTimer = undefined
    if (!playbackSucceeded) autoplayBlocked.value = true
  }, isWechat ? 1200 : 500)
}

/** 微信桥接回调已经到了：在回调里起播，失败才允许弹提示 */
const handleWechatAuthorized = () => {
  wechatBridgeAttempted = true
  blockedUiPending = false
  attemptPlayback('wechat-network-callback')
}

/** 兜底：没有桥接的微信环境（如电脑版微信、部分小程序 web-view），页面加载完 3 秒后照常提示 */
const releaseWechatBlockedUi = () => {
  window.setTimeout(() => {
    if (wechatBridgeAttempted) return
    wechatBridgeAttempted = true
    if (blockedUiPending && !playbackSucceeded) scheduleBlockedUi()
  }, 3000)
}

const attemptPlayback = (trigger: PlaybackTrigger) => {
  const video = videoElement.value
  if (!video) {
    logVideoPlaybackInfo(trigger, 'video element unavailable')
    return
  }

  const playPromise = video.play()
  if (!playPromise) {
    logVideoPlaybackInfo(trigger, 'play returned no Promise')
    return
  }

  playPromise
    .then(() => {
      playbackSucceeded = true
      cancelBlockedUi()
      autoplayBlocked.value = false
      removeInteractionRecovery()
      logVideoPlaybackSuccess(trigger)
    })
    .catch((error: unknown) => {
      if (playbackSucceeded) return
      const result = logVideoPlaybackError(trigger, error)
      if (result.name === 'NotAllowedError') {
        scheduleBlockedUi()
        installInteractionRecovery()
      }
    })
}

function handleUserInteraction() {
  attemptPlayback('user-interaction')
}

/* ---------- 播放看门狗 ----------
 * 手机上偶尔会出现：画面卡住不动、播几遍后黑屏、被系统悄悄暂停。
 * 每 2 秒看一次：页面在前台、视频在可视范围内、之前播成功过，但进度不走了，就重新加载再播。
 * 一分钟内最多自动恢复 3 次，避免在真坏掉的网络下无限重试。 */
const WATCHDOG_MS = 2000
let watchdogTimer: number | undefined
let lastTime = -1
let stuckTicks = 0
let recoverCount = 0
let recoverWindowStart = 0
let heroVisible = true
let viewportObserver: IntersectionObserver | undefined
let listenedVideo: HTMLVideoElement | null = null

const recoverPlayback = (reason: string) => {
  const video = videoElement.value
  if (!video) return
  const now = Date.now()
  if (now - recoverWindowStart > 60_000) {
    recoverWindowStart = now
    recoverCount = 0
  }
  if (recoverCount >= 3) return
  recoverCount += 1
  logVideoPlaybackInfo('recovery', reason)
  lastTime = -1
  stuckTicks = 0
  video.load()
  attemptPlayback('recovery')
}

const watchdogTick = () => {
  const video = videoElement.value
  if (!video || document.hidden || !heroVisible || !playbackSucceeded) {
    lastTime = -1
    stuckTicks = 0
    return
  }
  if (video.paused) {
    // 系统把视频暂停了（省电、切后台回来等），直接接着播
    attemptPlayback('recovery')
    return
  }
  if (video.readyState >= 2 && video.currentTime === lastTime) {
    stuckTicks += 1
    if (stuckTicks >= 2) recoverPlayback('playback stalled')
  } else {
    stuckTicks = 0
  }
  lastTime = video.currentTime
}

const handleEnded = () => {
  // loop 属性失效时的兜底：手动回到开头
  const video = videoElement.value
  if (!video) return
  video.currentTime = 0
  attemptPlayback('recovery')
}

const handleMediaError = () => recoverPlayback('media error')

const startWatchdog = (video: HTMLVideoElement) => {
  if (listenedVideo === video) return
  listenedVideo = video
  video.addEventListener('ended', handleEnded)
  video.addEventListener('error', handleMediaError)
  if (watchdogTimer === undefined) watchdogTimer = window.setInterval(watchdogTick, WATCHDOG_MS)

  // 首屏滚出视野时暂停，省电也减少解码压力；回到视野再播。
  // 微信里不做暂停：部分微信 WebView 暂停后需要重新走桥接授权才能再播。
  if (!isWechat && 'IntersectionObserver' in window) {
    viewportObserver = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting)
      heroVisible = visible
      if (visible) attemptPlayback('viewport')
      else if (playbackSucceeded) video.pause()
    }, { threshold: 0.05 })
    viewportObserver.observe(video)
  }
}

const stopWatchdog = () => {
  if (watchdogTimer !== undefined) window.clearInterval(watchdogTimer)
  watchdogTimer = undefined
  viewportObserver?.disconnect()
  viewportObserver = undefined
  listenedVideo?.removeEventListener('ended', handleEnded)
  listenedVideo?.removeEventListener('error', handleMediaError)
  listenedVideo = null
}

const handleVideoReady = (video: HTMLVideoElement) => {
  videoElement.value = video
  startWatchdog(video)
  attemptPlayback('video-ready')
}

const handleWechatBridgeReady = () => {
  attemptPlayback('wechat-bridge')

  // 部分微信 WebView 只有在原生桥接 API 的回调中才会重新评估媒体授权。
  if (wechatWindow.__openrareWechatAutoplay?.requested) return

  const bridge = wechatWindow.WeixinJSBridge
  bridge?.invoke?.('getNetworkType', {}, handleWechatAuthorized)
}

const handleVisibilityChange = () => {
  if (!document.hidden) attemptPlayback('visibility')
}

onMounted(() => {
  // 所有浏览器：切回前台时补一次播放（以前只在微信里监听）
  if (!isWechat) document.addEventListener('visibilitychange', handleVisibilityChange)
  if (!isWechat) return
  wechatWindow.__openrareWechatAutoplay?.subscribe(handleWechatAuthorized)
  if (document.readyState === 'complete') releaseWechatBlockedUi()
  else window.addEventListener('load', releaseWechatBlockedUi, { once: true })
  document.addEventListener('WeixinJSBridgeReady', handleWechatBridgeReady)
  document.addEventListener('visibilitychange', handleVisibilityChange)

  if (wechatWindow.WeixinJSBridge) handleWechatBridgeReady()
})

onBeforeUnmount(() => {
  stopWatchdog()
  cancelBlockedUi()
  removeInteractionRecovery()
  document.removeEventListener('WeixinJSBridgeReady', handleWechatBridgeReady)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('load', releaseWechatBlockedUi)
})
</script>

<template>
  <section
    class="hero-section"
    data-track-section="hero"
    :style="{ backgroundImage: `url(${config.media.heroBackground})` }"
  >
    <div class="hero-section__glow" aria-hidden="true"></div>
    <div class="hero-device">
      <InlineAutoplayVideo
        :src="config.media.heroVideo"
        :poster="config.media.heroVideoPoster"
        :label="copy.hero.videoLabel"
        @ready="handleVideoReady"
        @canplay="attemptPlayback('canplay')"
      />
      <img class="hero-device__frame" :src="config.media.phoneFrame" alt="" />
      <button
        v-if="autoplayBlocked"
        class="hero-device__play-recovery"
        type="button"
        :aria-label="copy.hero.playVideo"
        @click="attemptPlayback('user-interaction')"
      >
        <span class="hero-device__play-icon" aria-hidden="true">▶</span>
        <strong>{{ copy.hero.playVideo }}</strong>
        <small>{{ copy.hero.autoplayBlocked }}</small>
      </button>
    </div>
    <CtaButton
      track="hero-cta"
      class="hero-section__cta"
      :label="copy.hero.cta"
      :href="config.links.heroCta"
      :loading="copy.ctaLoading"
    />
  </section>
</template>
